/**
 * The call flow check, asked again a moment after the map stops changing.
 *
 * The map autosaves on its own (`useFlowMapDoc`); this hook only asks the
 * server whether the AI could follow the map on screen, so the editor marks a
 * broken step while it is still being drawn and not after a refused call. An
 * answer for a map that has changed since is dropped: only the newest request
 * may paint.
 */
import { useEffect, useRef, useState } from "react";
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { checkCallFlow, type CallFlowCheck } from "../../../../../lib/aiCallFlow";

/** Long enough for a drag or a typed title to finish, short enough to feel live. */
export const FLOW_CHECK_DELAY_MS = 500;

export type FlowCheckState =
  | { state: "idle" }
  | { state: "checking"; last: CallFlowCheck | null }
  | { state: "ready"; check: CallFlowCheck }
  | { state: "failed"; last: CallFlowCheck | null };

/** The check of `doc`; idle while there is no document or the viewer may not ask. */
export function useFlowCheck(doc: FlowMapDoc | null, enabled: boolean): FlowCheckState {
  const [result, setResult] = useState<FlowCheckState>({ state: "idle" });
  const seq = useRef(0);
  const last = useRef<CallFlowCheck | null>(null);

  useEffect(() => {
    const mine = ++seq.current;
    // Bumped even when there is nothing to ask, so a check already on its way cannot paint afterwards.
    if (!doc || !enabled) return;
    setResult({ state: "checking", last: last.current });
    const timer = setTimeout(() => {
      checkCallFlow(doc).then(
        (check) => {
          if (seq.current !== mine) return;
          last.current = check;
          setResult({ state: "ready", check });
        },
        () => {
          if (seq.current === mine) setResult({ state: "failed", last: last.current });
        },
      );
    }, FLOW_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [doc, enabled]);

  return result;
}

/** The newest check there is, while a newer one is on its way or after one failed. */
export function currentCheck(result: FlowCheckState): CallFlowCheck | null {
  if (result.state === "ready") return result.check;
  if (result.state === "checking" || result.state === "failed") return result.last;
  return null;
}
